import type {Questionnaire} from '@refinio/one.models/lib/models/QuestionnaireModel.js';

export const ProfileQuestionnaireDE: Questionnaire = {
    resourceType: 'Questionnaire',
    language: 'de',
    url: 'http://heyfreeda.com/questionnaire/profile/de',
    name: 'ProfileQuestionnaire',
    title: 'Persönliches Profil',
    status: 'active',
    item: [
        // ══════════════════════════════════════════════
        // Einleitung
        // ══════════════════════════════════════════════
        {
            linkId: 'intro',
            text: 'Einleitung',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'introText',
                    text: 'Liebe Teilnehmerin,\n\nwir möchten dich auf deinem Weg bestmöglich unterstützen. Dafür möchten wir ein persönliches Profil über dich erstellen, das zeigt, wer du bist, was dich auszeichnet und welchen Weg du gehen möchtest. Es kann uns helfen, mit möglichen Ausbildungsbetrieben, Partnern oder Unterstützern in Deutschland über deine Perspektiven zu sprechen. Damit dieses Profil ein klares und glaubwürdiges Bild von dir zeigt, haben wir diesen Fragebogen vorbereitet.',
                    type: 'display',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'introWhyText',
                    text: 'Warum ist das wichtig?\n\nViele Bewerbungen klingen sehr ähnlich. Oft stehen dort allgemeine Sätze wie: „Ich bin fleißig, pünktlich und lerne gern." Das ist nicht falsch, aber es bleibt oft wenig im Gedächtnis.\n\nHilfreicher sind konkrete und persönliche Antworten. Wenn du zum Beispiel beschreibst, wie dein Alltag aussieht, was dich motiviert oder warum dir ein bestimmter Weg wichtig ist, entsteht ein besseres Bild von dir als Person. So wird verständlicher, was dich ausmacht und warum du diesen Schritt gehen möchtest.',
                    type: 'display',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'introHintsText',
                    text: 'Wichtig vorab\n\nEs gibt keine falschen Antworten. Du kannst den Fragebogen auf Englisch ausfüllen. Wichtig ist vor allem, dass die Gedanken und Formulierungen von dir kommen. Beim sprachlichen Feinschliff unterstützen wir dich später.\n\nDiese Hinweise können dir beim Ausfüllen helfen:\n\n• Antworte möglichst konkret. Kurze Antworten sind in Ordnung. Noch hilfreicher wird es, wenn du kurz dazuschreibst, warum etwas für dich wichtig ist oder ein kleines Beispiel nennst.\n• Schreibe ehrlich und in deinen eigenen Worten. Du musst nichts schreiben, von dem du glaubst, dass wir es hören möchten. Am hilfreichsten sind Antworten, die wirklich zu dir passen.\n• Teile nur das, was sich für dich richtig anfühlt. Wenn es auf deinem Weg Schwierigkeiten oder Hindernisse gab, kannst du sie gern nennen. Du musst aber nichts Persönliches teilen, was du nicht erzählen möchtest.',
                    type: 'display',
                    disabledDisplay: 'protected'
                }
            ]
        },

        // ══════════════════════════════════════════════
        // A. Faktenfragen
        // ══════════════════════════════════════════════
        {
            linkId: 'facts',
            text: 'Faktenfragen',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'nameAgeLocation',
                    text: 'Wie heißt du, wie alt bist du und wo lebst du aktuell?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'education',
                    text: 'Was hast du bisher schulisch, im Studium oder in einer Ausbildung abgeschlossen?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'languageSkills',
                    text: 'Welche Sprachkenntnisse hast du aktuell, besonders in Deutsch?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'certificates',
                    text: 'Hast du ein offizielles Deutsch-Zertifikat oder andere Nachweise?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'whyGermany',
                    text: 'Warum möchtest du diesen Weg gehen \u2013 Ausbildung, Studium oder Arbeit \u2013 und warum in Deutschland?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'nextStep',
                    text: 'Was wäre der nächste wichtige Schritt für dich?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'idealStart',
                    text: 'Wann würdest du idealerweise mit einer Ausbildung, Arbeit oder einem Studium beginnen?',
                    type: 'string',
                    disabledDisplay: 'protected'
                }
            ]
        },

        // ══════════════════════════════════════════════
        // B. Kernfragen für dein Profil
        // ══════════════════════════════════════════════
        {
            linkId: 'coreProfile',
            text: 'Kernfragen für dein Profil',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'origin',
                    text: 'Deine Geschichte beginnt an dem Ort, aus dem du kommst \u2013 erzähl uns davon.',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'educationPath',
                    text: 'Wie sah dein Bildungsweg bisher aus?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'turningPoint',
                    text: 'Gab es einen Moment, in dem dir klar wurde, dass du mehr für deine Zukunft möchtest?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'typicalDay',
                    text: 'Beschreibe einen normalen Tag in deinem Leben.',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'roleModel',
                    text: 'Wer hat dich in deinem Leben besonders geprägt \u2013 und warum?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'motivation',
                    text: 'Was motiviert dich, auch wenn etwas schwierig ist?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'proud',
                    text: 'Worauf bist du stolz?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'future',
                    text: 'Wie stellst du dir deine Zukunft vor?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'aboutYou',
                    text: 'Was sollten wir unbedingt über dich wissen?',
                    type: 'string',
                    disabledDisplay: 'protected'
                }
            ]
        },

        // ══════════════════════════════════════════════
        // C. Ein bisschen was über dich
        // ══════════════════════════════════════════════
        {
            linkId: 'personal',
            text: 'Ein bisschen was über dich',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'favouriteFood',
                    text: 'Was ist dein Lieblingsessen?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'threeWords',
                    text: 'Mit welchen drei Worten würden deine Freunde oder Familie dich beschreiben?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'dailyJoy',
                    text: 'Worauf freust du dich jeden Tag?',
                    type: 'string',
                    disabledDisplay: 'protected'
                },
                {
                    linkId: 'favouriteMedia',
                    text: 'Welches Buch, Lied oder welcher Film gefällt dir besonders?',
                    type: 'string',
                    disabledDisplay: 'protected'
                }
            ]
        },

        // ══════════════════════════════════════════════
        // Fotos
        // ══════════════════════════════════════════════
        {
            linkId: 'photos',
            text: 'Fotos',
            type: 'group',
            disabledDisplay: 'protected',
            item: [
                {
                    linkId: 'photosText',
                    text: 'Bitte schicke uns zusätzlich aktuelle Fotos, die dich oder deinen Alltag zeigen. Zum Beispiel:\n\n• Porträtfotos von dir\n• Bilder aus deinem Alltag\n• Fotos aus deinem Wohnort oder Umfeld\n• Bilder aus Schule, Studium, Kurs oder Lernen\n\nBitte schicke nur Fotos, mit denen du dich wohlfühlst und die wir verwenden dürfen. Andere Personen sollten nur dann auf den Bildern zu sehen sein, wenn das für sie in Ordnung ist.\n\nSchicke die Fotos bitte per WhatsApp an Janine: +49 15141472058',
                    type: 'display',
                    disabledDisplay: 'protected'
                }
            ]
        }
    ]
};
